import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Brain, MessageSquare, Upload, Shield, Zap, Globe, TrendingUp, Sparkles, CheckCircle, Rocket } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navigation from '../components/Navigation';

export default function FeaturesPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Brain,
      title: '268+ AI Tools Hub',
      desc: 'Access the largest collection of AI tools across 27 categories. From image generation to code completion, find the perfect tool for your needs.',
      gradient: 'from-blue-500 to-cyan-500',
      benefits: ['27 AI categories', 'Real-time updates', 'Community ratings', 'API access']
    },
    {
      icon: MessageSquare,
      title: 'Real-time Chat & Collaboration',
      desc: 'Connect with vendors, creators, and buyers instantly. Firebase-powered messaging ensures zero-latency communication.',
      gradient: 'from-purple-500 to-pink-500',
      benefits: ['Instant messaging', 'File sharing', 'Group chats', 'Read receipts']
    },
    {
      icon: TrendingUp,
      title: 'Live Auctions',
      desc: 'Participate in real-time auctions with WebSocket technology. Bid on exclusive AI models, datasets, and custom solutions.',
      gradient: 'from-teal-500 to-cyan-500',
      benefits: ['Real-time bidding', 'Auto-bid', 'Bid history', 'Winner notifications']
    },
    {
      icon: Upload,
      title: 'Cloud Storage Integration',
      desc: 'Upload and manage files with Firebase Storage and Cloudflare R2. Seamless integration with your AI workflows.',
      gradient: 'from-blue-500 to-purple-500',
      benefits: ['Unlimited uploads', 'CDN delivery', 'Version control', 'Secure access']
    },
    {
      icon: Shield,
      title: 'Secure Payments',
      desc: 'Stripe Live Mode integration with buyer protection, instant payouts, and subscription management.',
      gradient: 'from-cyan-500 to-teal-500',
      benefits: ['Stripe powered', 'Buyer protection', 'Instant payouts', 'Multi-currency']
    },
    {
      icon: Rocket,
      title: 'AI Agent Assistance',
      desc: 'Autonomous agents powered by Gemini 2.5 Flash provide content moderation, support, and intelligent recommendations.',
      gradient: 'from-purple-500 to-blue-500',
      benefits: ['24/7 support', 'Auto-moderation', 'Smart search', 'Personalized feed']
    }
  ];

  const advancedFeatures = [
    {
      title: 'Voice UI',
      desc: 'Speech-to-text, text-to-speech, and voice-to-voice pipelines powered by OpenAI',
      icon: '🎤'
    },
    {
      title: 'Cloudflare Workers AI',
      desc: 'Serverless AI inference at the edge for ultra-low latency',
      icon: '⚡'
    },
    {
      title: 'Firebase Firestore',
      desc: 'Real-time database with automatic sync across all devices',
      icon: '🔥'
    },
    {
      title: 'Autonomous Workflows',
      desc: 'Self-healing systems with auto-discovery and troubleshooting',
      icon: '🤖'
    },
    {
      title: 'TerraForge Studio',
      desc: 'AI-powered landscaping with 1000+ plant species',
      icon: '🌳'
    },
    {
      title: 'API Access',
      desc: 'RESTful API with comprehensive documentation',
      icon: '🔌'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/20 to-black"></div>
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <Navigation />

      <div className="relative z-10 pt-24">
        {/* Hero Section */}
        <div className="py-32">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-cyan-400 font-medium">Production-Ready Features</span>
              </div>

              <h1 className="text-6xl md:text-7xl font-black mb-6">
                Everything You Need
                <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  in One Platform
                </span>
              </h1>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                From AI tools to real-time auctions, we've built the most comprehensive 
                platform for the AI economy
              </p>
            </motion.div>
          </div>
        </div>

        {/* Main Features */}
        <div className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-8 rounded-3xl bg-zinc-900/50 border border-zinc-800 hover:border-purple-500/50 transition-all group"
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} p-4 mb-6 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-full h-full text-white" />
                  </div>

                  <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                  <p className="text-gray-400 mb-6 leading-relaxed">{feature.desc}</p>

                  <div className="grid grid-cols-2 gap-3">
                    {feature.benefits.map((benefit, benefitIdx) => (
                      <div key={benefitIdx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                        <span className="text-sm text-gray-300">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Advanced Features */}
        <div className="py-20 border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-black mb-6">Advanced Features</h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Enterprise-grade capabilities for power users
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {advancedFeatures.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-6 rounded-2xl bg-zinc-900/30 border border-zinc-800 hover:border-cyan-500/50 transition-all"
                >
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Integration Section */}
        <div className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-black mb-6">Powered by the Best</h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Integrations with industry-leading platforms
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                'Firebase',
                'Cloudflare',
                'Stripe',
                'OpenAI',
                'Gemini',
                'Claude',
                'MongoDB',
                'GitHub'
              ].map((integration, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 text-center hover:border-purple-500/50 transition-all"
                >
                  <Globe className="w-8 h-8 mx-auto mb-2 text-cyan-400" />
                  <div className="font-semibold">{integration}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-32">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-purple-600 to-cyan-600 p-12 md:p-20 text-center"
            >
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative z-10">
                <h2 className="text-4xl md:text-6xl font-black mb-6">
                  Ready to Experience It?
                </h2>
                <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                  Start with our free plan and upgrade as you grow
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    onClick={() => navigate('/register')}
                    className="bg-black hover:bg-zinc-900 text-white px-10 py-6 text-lg rounded-full"
                  >
                    Get Started Free
                  </Button>
                  <Button
                    size="lg"
                    onClick={() => navigate('/pricing')}
                    className="bg-white/10 hover:bg-white/20 text-white px-10 py-6 text-lg rounded-full border border-white/20"
                  >
                    View Pricing
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}